import { SYSTEM_VARIABLES } from "@velizhanin-dev/bot-scenario"
import type { Editor } from "@tiptap/react"
import { Braces } from "lucide-react"
import { useRef, useState } from "react"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"
import { Textarea } from "@/shared/ui/textarea"
import { RichTextEditor } from "./rich-text-editor"
import type { RichTextFormat } from "@/shared/lib/rich-text"
import type { SelectableVariable } from "./variable-select"

/** Текст с кнопкой вставки ${переменной} в позицию курсора; с rich форматирование видно сразу. */
export function TextWithVariables({
	value,
	onChange,
	variables,
	multiline = true,
	rows = 4,
	placeholder,
	id,
	maxLength,
	rich,
}: {
	value: string
	onChange: (value: string) => void
	variables: SelectableVariable[]
	multiline?: boolean
	rows?: number
	placeholder?: string
	id?: string
	maxLength?: number
	/** Форматирование видно сразу: текст хранится в этом формате (у бота HTML Telegram). */
	rich?: RichTextFormat
}) {
	const editorRef = useRef<Editor | null>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState("")

	const insert = (name: string) => {
		setOpen(false)
		const token = `\${${name}}`
		if (rich && editorRef.current) {
			editorRef.current.chain().focus().insertContent(token).run()
			return
		}
		const element = multiline ? textareaRef.current : inputRef.current
		const start = element?.selectionStart ?? value.length
		const end = element?.selectionEnd ?? value.length
		onChange(value.slice(0, start) + token + value.slice(end))
		requestAnimationFrame(() => {
			element?.focus()
			element?.setSelectionRange(start + token.length, start + token.length)
		})
	}

	return (
		<div className="space-y-1">
			{rich ? (
				<RichTextEditor
					id={id}
					value={value}
					onChange={onChange}
					format={rich}
					placeholder={placeholder}
					minHeight={`${rows * 1.5 + 1}rem`}
					editorRef={editorRef}
				/>
			) : multiline ? (
				<Textarea
					ref={textareaRef}
					id={id}
					rows={rows}
					value={value}
					maxLength={maxLength}
					placeholder={placeholder}
					onChange={(event) => onChange(event.target.value)}
				/>
			) : (
				<Input
					ref={inputRef}
					id={id}
					value={value}
					maxLength={maxLength}
					placeholder={placeholder}
					onChange={(event) => onChange(event.target.value)}
				/>
			)}
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs">
						<Braces />
						Вставить переменную
					</Button>
				</PopoverTrigger>
				<PopoverContent align="start" className="max-h-72 w-72 overflow-y-auto p-2">
					<Input
						type="search"
						placeholder="Найти переменную"
						aria-label="Поиск переменной для вставки"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
					/>
					{variables.length > 0 ? (
						<p className="px-2 pt-1 pb-0.5 text-[11px] uppercase text-muted-foreground">Сценария</p>
					) : null}
					{variables
						.filter((variable) =>
							variable.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
						)
						.map((variable) => (
							<VariableOption key={variable.id} name={variable.name} onPick={insert} />
						))}
					<p className="px-2 pt-2 pb-0.5 text-[11px] uppercase text-muted-foreground">Системные</p>
					{SYSTEM_VARIABLES.filter((variable) =>
						variable.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
					).map((variable) => (
						<VariableOption
							key={variable.name}
							name={variable.name}
							description={variable.description}
							onPick={insert}
						/>
					))}
				</PopoverContent>
			</Popover>
		</div>
	)
}

function VariableOption({
	name,
	description,
	onPick,
}: {
	name: string
	description?: string
	onPick: (name: string) => void
}) {
	return (
		<Button
			type="button"
			variant="ghost"
			className="h-auto w-full flex-col items-start justify-start rounded-md px-2 py-1.5 text-left"
			onClick={() => onPick(name)}
		>
			<span className="block font-mono text-xs">{name}</span>
			{description ? (
				<span className="block text-[11px] text-muted-foreground">{description}</span>
			) : null}
		</Button>
	)
}
